import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService } from '../../services/projectService';
import clinicalIntakeService from '../../services/clinicalIntake.service';
import { useProject } from '../../context/ProjectContext';
import PolicyDeniedPane from '../access/PolicyDeniedPane';
import { parsePolicyDenialResponse } from '../../utils/policyViewerDenial';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Tabs,
    Tab,
    Card,
    CardContent,
    CardActions,
    IconButton,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderIcon from '@mui/icons-material/Folder';
import HistoryIcon from '@mui/icons-material/History';
//import AddIcon from '@mui/icons-material/Add';
//import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { projectListPrimaryLabel } from '../../utils/projectIdentityFields';
import PageHeader from '../common/PageHeader';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';

const ProjectDetail = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { loadProject, currentProject: contextProject } = useProject();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deniedReason, setDeniedReason] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [forms, setForms] = useState([]);
    const [formsLoading, setFormsLoading] = useState(false);
    const [formsError, setFormsError] = useState(null);
    //const [openFormDialog, setOpenFormDialog] = useState(false);
    //const [selectedForm, setSelectedForm] = useState(null);

    useEffect(() => {
        const fetchProject = async () => {
            console.log('Attempting to fetch project with ID:', projectId);
            try {
                setLoading(true);
                setError(null);
                setDeniedReason(null);
                const response = await projectService.getProject(projectId);
                setProject(response.data);
                console.log('Project fetched successfully:', response.data);
            } catch (err) {
                const denial = parsePolicyDenialResponse(err, {
                    defaultAction: 'study:open',
                    forbiddenFallbackReason: 'You do not have access to this study.',
                });
                if (denial) {
                    setDeniedReason(denial.reason);
                } else {
                    setError('Failed to fetch project details');
                }
                console.error('Error fetching project details:', err);
            } finally {
                setLoading(false);
                console.log('Project loading finished. Loading state:', false);
            }
        };

        if (projectId) {
            fetchProject();
        }
    }, [projectId, loadProject]);

    useEffect(() => {
        if (project && (!contextProject || contextProject._id !== project._id)) {
            console.log('ProjectDetail: Updating ProjectContext with project:', project._id);
            loadProject(project._id);
        }
    }, [project, contextProject, loadProject]);

    useEffect(() => {
        const fetchForms = async () => {
            if (project) {
                setFormsLoading(true);
                setFormsError(null);
                try {
                    const response = await clinicalIntakeService.getProjectForms(project._id);
                    setForms(response.data);
                } catch (err) {
                    console.error('Failed to fetch forms:', err);
                    setFormsError('Failed to load clinical intake forms.');
                } finally {
                    setFormsLoading(false);
                }
            }
        };

        fetchForms();
    }, [project]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleBack = () => {
        navigate('/projects');
    };

    const handleCreateForm = () => {
        navigate(`/clinical-intake?project=${project._id}`);
    };

    const handleEditForm = (formId) => {
        navigate(`/clinical-intake?formId=${formId}&project=${project._id}`);
    };

    const handleViewForm = (formId) => {
        navigate(`/clinical-intake?formId=${formId}&project=${project._id}&viewMode=true`);
    };

    const handleDeleteForm = async (formId) => {
        if (window.confirm('Are you sure you want to delete this form?')) {
            try {
                await clinicalIntakeService.deleteForm(formId);
                setForms(forms.filter(form => form._id !== formId));
            } catch (err) {
                console.error('Failed to delete form:', err);
            }
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, mt: 2 }}>
                <LoadingState label="Loading project…" />
            </Box>
        );
    }

    if (deniedReason) {
        return <PolicyDeniedPane reason={deniedReason} size="wide" />;
    }

    if (error) {
        return (
            <Box sx={{ p: 3, mt: 2 }}>
                <ErrorState message={error} />
            </Box>
        );
    }

    if (!project) {
        return (
            <Box sx={{ p: 3, mt: 2 }}>
                <ErrorState title="Project not found" message="This project may have been removed or you may not have access to it." />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, mt:2 }}>
            <PageHeader
                onBack={handleBack}
                title={projectListPrimaryLabel(project)}
                breadcrumbs={[{ label: 'Projects', onClick: handleBack }, { label: projectListPrimaryLabel(project) }]}
            />

            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                            {project.status ? <StatusBadge status={project.status} /> : <Typography color="text.secondary">—</Typography>}
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Created
                        </Typography>
                        <Typography sx={{ mt: 0.5 }}>
                            {format(new Date(project.createdAt), 'MMM dd, yyyy')}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ width: '100%' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab
                        icon={<DescriptionIcon />}
                        label="Clinical Intake Forms"
                        iconPosition="start"
                    />
                    <Tab
                        icon={<AssignmentIcon />}
                        label="Document Editor"
                        iconPosition="start"
                    />
                    <Tab
                        icon={<FolderIcon />}
                        label="Forms"
                        iconPosition="start"
                    />
                    <Tab
                        icon={<HistoryIcon />}
                        label="History"
                        iconPosition="start"
                    />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {activeTab === 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="h6">
                                    Clinical Intake Forms
                                </Typography>
                                <Button
                                    variant="contained"
                                    //startIcon={<AddIcon />}
                                    onClick={handleCreateForm}
                                >
                                    view
                                </Button>
                            </Box>

                            {formsLoading ? (
                                <LoadingState label="Loading forms…" />
                            ) : formsError ? (
                                <ErrorState message={formsError} />
                            ) : forms.length === 0 ? (
                                <EmptyState
                                    icon={<DescriptionIcon fontSize="medium" />}
                                    title="No clinical intake forms yet"
                                    description={`Click "view" above to create one for this project.`}
                                />
                            ) : (
                                <Grid container spacing={2.5}>
                                    {forms.map((form) => (
                                        <Grid item xs={12} sm={6} md={4} key={form._id}>
                                            <Card sx={{ height: '100%' }}>
                                                <CardContent>
                                                    <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                                                        {form.study_identification?.protocol_number || 'Unnamed Form'}
                                                    </Typography>
                                                    {form.status && (
                                                        <Box sx={{ mb: 1 }}>
                                                            <StatusBadge status={form.status} />
                                                        </Box>
                                                    )}
                                                    <Typography variant="body2" color="text.secondary">
                                                        Created {format(new Date(form.createdAt), 'MMM dd, yyyy')}
                                                    </Typography>
                                                </CardContent>
                                                <CardActions sx={{ px: 2, pb: 2 }}>
                                                    <Button
                                                        size="small"
                                                        onClick={() => handleViewForm(form._id)}
                                                    >
                                                        View
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        onClick={() => handleEditForm(form._id)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Box sx={{ flexGrow: 1 }} />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteForm(form._id)}
                                                    >
                                                        <DeleteIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    )}
                    {activeTab === 1 && (
                        <EmptyState
                            icon={<AssignmentIcon fontSize="medium" />}
                            title="Document Editor"
                            description="Open a document from Clinical Intake Forms to start editing."
                        />
                    )}
                    {activeTab === 2 && (
                        <EmptyState
                            icon={<FolderIcon fontSize="medium" />}
                            title="Forms"
                            description="No additional forms configured yet."
                        />
                    )}
                    {activeTab === 3 && (
                        <EmptyState
                            icon={<HistoryIcon fontSize="medium" />}
                            title="History"
                            description="Version history will appear here once changes are made."
                        />
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ProjectDetail; 